// ══════════════════════════════════════════════════════════
// DISCOVERY SERVICE
//
// Descubrimiento público de radios — secciones curadas,
// tendencias, nuevas, destacadas, verificadas, cercanas.
//
// NO incluye catálogo musical ni búsqueda de canciones.
// ══════════════════════════════════════════════════════════

import { getSupabaseClient } from '@/services/supabase/client';
import type {
  RadioSearchResult,
  DiscoverySection,
  DiscoverySectionType,
  DiscoveryFilters,
  DiscoverySummary,
  FrequencySlot,
  TunerState,
  TunerNavigationResult,
} from '@/types/discovery';

// ── Constantes del sintonizador ──

export const TUNER_MIN_FREQ = 87.0;
export const TUNER_MAX_FREQ = 200.0;
export const TUNER_STEP = 0.01;

// ── Helpers ──

function supabase() {
  return getSupabaseClient();
}

function mapRow(row: Record<string, unknown>): RadioSearchResult {
  return {
    radio_id: row.id as string,
    name: row.name as string,
    slug: row.slug as string,
    logo_url: (row.logo_url as string) || null,
    cover_url: (row.cover_url as string) || null,
    frequency: (row.frequency as string) || null,
    dial: (row.dial as string) || null,
    genre: (row.genre as string) || null,
    language: (row.language as string) || 'es',
    country: (row.country as string) || null,
    state: (row.state as string) || null,
    city: (row.city as string) || null,
    is_live: (row.is_live as boolean) || false,
    is_verified: (row.is_verified as boolean) || false,
    is_featured: (row.is_featured as boolean) || false,
    is_premium: (row.is_premium as boolean) || false,
    is_official: (row.is_official as boolean) || (row.is_platform_radio as boolean) || false,
    is_followed: (row.is_followed as boolean) || false,
    stream_available: !!(row.stream_url as string | null),
    current_program: (row.current_program as string) || null,
    current_announcer: (row.current_announcer as string) || null,
    listener_count: (row.listener_count as number) || 0,
    follower_count: (row.follower_count as number) || 0,
    radio_score: (row.radio_score as number) || 0,
    rating: (row.rating as number) || 0,
  };
}

// ═══════════════ DISCOVERY SUMMARY ═══════════════

export async function getDiscoverySummary(): Promise<DiscoverySummary> {
  const client = supabase();

  const { data, error } = await client
    .from('radios')
    .select('id, is_verified, country, genre, metadata')
    .eq('is_public', true)
    .eq('is_active', true);

  if (error) {
    throw new Error(`Error al obtener summary: ${error.message}`);
  }

  const rows = data || [];
  const countries = new Set<string>();
  const genres = new Set<string>();
  let liveCount = 0;
  let totalListeners = 0;

  rows.forEach((r: Record<string, unknown>) => {
    if (r.country) countries.add(r.country as string);
    if (r.genre) genres.add(r.genre as string);
    const meta = r.metadata as Record<string, unknown> | null;
    if (meta) {
      if (meta.is_live) liveCount++;
      if (typeof meta.listener_count === 'number') totalListeners += meta.listener_count as number;
    }
  });

  return {
    total_radios: rows.length,
    live_count: liveCount,
    verified_count: rows.filter((r) => r.is_verified).length,
    total_listeners: totalListeners,
    countries_count: countries.size,
    genres_count: genres.size,
  };
}

// ═══════════════ SECCIONES DE DISCOVERY ═══════════════

/**
 * Nuevas radios — ordenadas por fecha de creación.
 */
export async function getNewRadios(limit: number = 8): Promise<DiscoverySection> {
  const client = supabase();

  const { data, error } = await client
    .from('radios')
    .select('*')
    .eq('is_public', true)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(`Error al obtener radios nuevas: ${error.message}`);

  return {
    id: 'new',
    title: 'Nuevas Radios',
    subtitle: 'Las emisoras que acaban de unirse a la plataforma.',
    icon: 'ri-sparkling-line',
    section_type: 'new',
    radios: (data || []).map((r) => mapRow(r as Record<string, unknown>)),
  };
}

/**
 * Radios en tendencia — basado en listener_count del metadata.
 */
export async function getTrendingRadios(limit: number = 8): Promise<DiscoverySection> {
  const client = supabase();

  const { data, error } = await client
    .from('radios')
    .select('*')
    .eq('is_public', true)
    .eq('is_active', true)
    .order('metadata->listener_count', { ascending: false, nullsFirst: false })
    .limit(limit);

  if (error) throw new Error(`Error al obtener tendencias: ${error.message}`);

  return {
    id: 'trending',
    title: 'Tendencia',
    subtitle: 'Las estaciones que están marcando la conversación ahora mismo.',
    icon: 'ri-fire-line',
    section_type: 'trending',
    radios: (data || []).map((r) => mapRow(r as Record<string, unknown>)),
  };
}

/**
 * Radios destacadas — is_featured = true (vía metadata o campo dedicado).
 */
export async function getFeaturedRadios(limit: number = 8): Promise<DiscoverySection> {
  const client = supabase();

  const { data, error } = await client
    .from('radios')
    .select('*')
    .eq('is_public', true)
    .eq('is_active', true)
    .eq('is_verified', true)
    .order('name', { ascending: true })
    .limit(limit);

  if (error) throw new Error(`Error al obtener destacadas: ${error.message}`);

  const enriched = (data || []).map((r) => {
    const result = mapRow(r as Record<string, unknown>);
    result.is_featured = true;
    return result;
  });

  return {
    id: 'featured',
    title: 'Radios Destacadas',
    subtitle: 'Las emisoras verificadas con la mejor calidad de la plataforma.',
    icon: 'ri-star-fill',
    section_type: 'featured',
    radios: enriched,
  };
}

/**
 * Radios verificadas.
 */
export async function getVerifiedRadios(limit: number = 8): Promise<DiscoverySection> {
  const client = supabase();

  const { data, error } = await client
    .from('radios')
    .select('*')
    .eq('is_public', true)
    .eq('is_active', true)
    .eq('is_verified', true)
    .order('name', { ascending: true })
    .limit(limit);

  if (error) throw new Error(`Error al obtener verificadas: ${error.message}`);

  return {
    id: 'verified',
    title: 'Radios Verificadas',
    subtitle: 'Emisoras con el sello de verificación RFW.',
    icon: 'ri-verified-badge-line',
    section_type: 'verified',
    radios: (data || []).map((r) => mapRow(r as Record<string, unknown>)),
  };
}

/**
 * Radios con mejor radio_score.
 */
export async function getBestScoreRadios(limit: number = 8): Promise<DiscoverySection> {
  const client = supabase();

  const { data, error } = await client
    .from('radios')
    .select('*')
    .eq('is_public', true)
    .eq('is_active', true)
    .order('metadata->radio_score', { ascending: false, nullsFirst: false })
    .limit(limit);

  if (error) throw new Error(`Error al obtener mejor score: ${error.message}`);

  return {
    id: 'best_score',
    title: 'Mejor Radio Score',
    subtitle: 'Ranking por calidad general: oyentes, engagement y consistencia.',
    icon: 'ri-trophy-line',
    section_type: 'best_score',
    radios: (data || []).map((r) => mapRow(r as Record<string, unknown>)),
  };
}

/**
 * Radios más activas — con más estaciones activas o lives recientes.
 */
export async function getMostActiveRadios(limit: number = 8): Promise<DiscoverySection> {
  const client = supabase();

  const { data, error } = await client
    .from('radios')
    .select(
      `
      *,
      stations(count)
    `,
    )
    .eq('is_public', true)
    .eq('is_active', true)
    .order('name', { ascending: true })
    .limit(limit);

  if (error) throw new Error(`Error al obtener más activas: ${error.message}`);

  const enriched = (data || []).map((r: Record<string, unknown>) => {
    const result = mapRow(r);
    const stations = r.stations as Record<string, unknown>[] | { count: number } | null;
    if (stations && Array.isArray(stations)) {
      result.listener_count = stations.length; // proxy: más estaciones = más activa
    }
    return result;
  });

  return {
    id: 'most_active',
    title: 'Más Activas',
    subtitle: 'Las radios con mayor nivel de actividad en la plataforma.',
    icon: 'ri-flashlight-line',
    section_type: 'most_active',
    radios: enriched,
  };
}

/**
 * Obtener todas las secciones de discovery en una sola llamada.
 */
export async function getAllDiscoverySections(): Promise<DiscoverySection[]> {
  const [newRadios, trending, featured, verified, bestScore, mostActive] =
    await Promise.all([
      getNewRadios(6),
      getTrendingRadios(6),
      getFeaturedRadios(6),
      getVerifiedRadios(6),
      getBestScoreRadios(6),
      getMostActiveRadios(6),
    ]);

  return [newRadios, trending, featured, verified, bestScore, mostActive];
}

// ═══════════════ SINTONIZADOR ═══════════════

/**
 * Obtener la radio oficial de plataforma activa.
 * Consulta radios con is_platform_radio = true.
 * Sirve como fallback global del sintonizador.
 */
export async function getPlatformRadio(): Promise<RadioSearchResult | null> {
  const client = supabase();

  const { data, error } = await client
    .from('radios')
    .select('*')
    .eq('is_platform_radio', true)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.error('[getPlatformRadio] Error:', error.message);
    return null;
  }

  if (!data) return null;
  return mapRow(data as Record<string, unknown>);
}

/**
 * Verificar si la radio de plataforma tiene una estación activa con stream.
 */
export async function getPlatformRadioStatus(): Promise<{
  configured: boolean;
  has_active_station: boolean;
  stream_available: boolean;
  radio: RadioSearchResult | null;
}> {
  const radio = await getPlatformRadio();

  if (!radio) {
    return { configured: false, has_active_station: false, stream_available: false, radio: null };
  }

  // Verificar estación activa
  const client = supabase();
  const { data: stations, error: stErr } = await client
    .from('stations')
    .select('status, stream_url')
    .eq('radio_id', radio.radio_id)
    .eq('status', 'ONLINE');

  if (stErr) {
    return { configured: true, has_active_station: false, stream_available: false, radio };
  }

  const hasActiveStation = (stations || []).length > 0;
  const streamAvailable = hasActiveStation && (stations || []).some((s) => !!s.stream_url);

  return {
    configured: true,
    has_active_station: hasActiveStation,
    stream_available: streamAvailable,
    radio,
  };
}

/**
 * Obtener todas las frecuencias ocupadas activas.
 * Usa radios.frequency + frequency_assignments para cubrir
 * el rango 87.00 - 200.00.
 *
 * EXCLUYE radios de plataforma (is_platform_radio = true).
 */
export async function getOccupiedFrequencies(): Promise<FrequencySlot[]> {
  const client = supabase();

  // Radios con frequency directa (excluyendo platform radios)
  const { data: radios, error: radioErr } = await client
    .from('radios')
    .select('id, name, slug, logo_url, frequency, dial, is_platform_radio')
    .eq('is_public', true)
    .eq('is_active', true)
    .eq('is_platform_radio', false)
    .not('frequency', 'is', null);

  if (radioErr) throw new Error(`Error al obtener frecuencias: ${radioErr.message}`);

  // Frequency assignments activas (excluyendo platform radios vía JOIN)
  const { data: assignments, error: faErr } = await client
    .from('frequency_assignments')
    .select('radio_id, frequency, radios!inner(is_platform_radio)')
    .eq('is_active', true)
    .eq('radios.is_platform_radio', false);

  if (faErr) throw new Error(`Error al obtener asignaciones: ${faErr.message}`);

  const slotMap = new Map<number, FrequencySlot>();

  // Procesar radios con frequency directa
  (radios || []).forEach((r: Record<string, unknown>) => {
    const freq = parseFloat(r.frequency as string);
    if (isNaN(freq)) return;
    const key = Math.round(freq * 100) / 100;

    slotMap.set(key, {
      frequency: key,
      radio_id: r.id as string,
      radio_name: r.name as string,
      radio_slug: r.slug as string,
      logo_url: (r.logo_url as string) || null,
      is_occupied: true,
      is_live: false,
    });
  });

  // Procesar frequency_assignments (que pueden tener frecuencias distintas)
  (assignments || []).forEach((fa: Record<string, unknown>) => {
    const freq = fa.frequency as number;
    const key = Math.round(freq * 100) / 100;

    if (!slotMap.has(key)) {
      slotMap.set(key, {
        frequency: key,
        radio_id: fa.radio_id as string,
        radio_name: null,
        radio_slug: null,
        logo_url: null,
        is_occupied: true,
        is_live: false,
      });
    }
  });

  return Array.from(slotMap.values()).sort((a, b) => a.frequency - b.frequency);
}

/**
 * Obtener el estado actual del sintonizador.
 * Inicia en 87.00 y busca la radio activa asignada.
 *
 * Si la frecuencia está vacía:
 * - Busca la radio de plataforma (is_platform_radio = true)
 * - La usa como fallback global
 *
 * NO asigna frecuencia a la radio de plataforma.
 */
export async function getTunerState(frequency: number = TUNER_MIN_FREQ): Promise<TunerState> {
  const freq = Math.round(frequency * 100) / 100;
  const freqStr = freq.toFixed(2);

  const client = supabase();

  // Buscar radio en esta frecuencia exacta (NO platform radios)
  const { data: radio } = await client
    .from('radios')
    .select('*')
    .eq('is_public', true)
    .eq('is_active', true)
    .eq('is_platform_radio', false)
    .or(`frequency.eq.${freqStr},dial.eq.${freqStr}`)
    .maybeSingle();

  // También buscar vía frequency_assignments
  let assignedRadio: RadioSearchResult | null = null;
  if (!radio) {
    const freqNum = parseFloat(freqStr);
    if (!isNaN(freqNum)) {
      const { data: fa } = await client
        .from('frequency_assignments')
        .select('radio_id, radios!inner(is_platform_radio)')
        .eq('frequency', freqNum)
        .eq('is_active', true)
        .eq('radios.is_platform_radio', false)
        .maybeSingle();

      if (fa) {
        const { data: faRadio } = await client
          .from('radios')
          .select('*')
          .eq('id', (fa as Record<string, unknown>).radio_id as string)
          .eq('is_public', true)
          .eq('is_active', true)
          .eq('is_platform_radio', false)
          .maybeSingle();

        if (faRadio) {
          assignedRadio = mapRow(faRadio as Record<string, unknown>);
        }
      }
    }
  }

  const currentRadio = radio
    ? mapRow(radio as Record<string, unknown>)
    : assignedRadio;

  // Determinar estado del fallback
  let fallbackRadio: RadioSearchResult | null = null;
  let fallbackStatus: TunerState['fallback_status'] = 'NOT_CONFIGURED';

  if (!currentRadio) {
    const platformStatus = await getPlatformRadioStatus();

    if (platformStatus.configured && platformStatus.radio) {
      fallbackRadio = platformStatus.radio;

      if (platformStatus.stream_available) {
        fallbackStatus = 'AVAILABLE';
      } else if (platformStatus.has_active_station) {
        fallbackStatus = 'OFFLINE';
      } else {
        fallbackStatus = 'UNAVAILABLE';
      }
    } else {
      fallbackStatus = 'NOT_CONFIGURED';
    }
  }

  return {
    current_frequency: freq,
    min_frequency: TUNER_MIN_FREQ,
    max_frequency: TUNER_MAX_FREQ,
    step: TUNER_STEP,
    current_radio: currentRadio,
    is_free_frequency: !currentRadio,
    fallback_radio: fallbackRadio,
    fallback_status: fallbackStatus,
    display_frequency_label: freqStr,
  };
}

/**
 * Navegar a la siguiente o anterior frecuencia ocupada.
 * Avanza/retrocede hasta encontrar una radio activa.
 *
 * EXCLUYE radios de plataforma del conteo de ocupadas.
 * Continúa después de 200.00 (wraps to 87.00).
 */
export async function navigateTuner(
  currentFreq: number,
  direction: 'next' | 'prev',
): Promise<TunerNavigationResult> {
  const occupied = await getOccupiedFrequencies();
  const freq = Math.round(currentFreq * 100) / 100;

  if (direction === 'next') {
    const next = occupied.find((s) => s.frequency > freq);
    if (next) {
      return { frequency: next.frequency, radio: null, direction: 'next', is_free: false };
    }
    // Wrap around
    if (occupied.length > 0) {
      const first = occupied[0];
      return { frequency: first.frequency, radio: null, direction: 'next', is_free: false };
    }
    // No occupied frequencies at all — advance past 200.00
    const wrapped = freq + TUNER_STEP > TUNER_MAX_FREQ ? TUNER_MIN_FREQ : freq + TUNER_STEP;
    return { frequency: Math.round(wrapped * 100) / 100, radio: null, direction: 'next', is_free: true };
  }

  // prev
  const prev = [...occupied].reverse().find((s) => s.frequency < freq);
  if (prev) {
    return { frequency: prev.frequency, radio: null, direction: 'prev', is_free: false };
  }
  // Wrap around
  if (occupied.length > 0) {
    const last = occupied[occupied.length - 1];
    return { frequency: last.frequency, radio: null, direction: 'prev', is_free: false };
  }
  const wrapped = freq - TUNER_STEP < TUNER_MIN_FREQ ? TUNER_MAX_FREQ : freq - TUNER_STEP;
  return { frequency: Math.round(wrapped * 100) / 100, radio: null, direction: 'prev', is_free: true };
}

// ═══════════════ EXPORT AGRUPADO ═══════════════

export const discoveryService = {
  getDiscoverySummary,
  getNewRadios,
  getTrendingRadios,
  getFeaturedRadios,
  getVerifiedRadios,
  getBestScoreRadios,
  getMostActiveRadios,
  getAllDiscoverySections,
  getOccupiedFrequencies,
  getTunerState,
  navigateTuner,
  getPlatformRadio,
  getPlatformRadioStatus,
};